import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { competitors as api, llmMentions as llmMentionsApi } from "../../api/endpoints";
import ModelBadge from "../../components/ModelBadge.jsx";
import SiteFavicon from "../../components/SiteFavicon.jsx";
import { useSelectedWebsite } from "./Layout.jsx";

export default function Competitors() {
  const { t } = useTranslation();
  const { websiteId } = useSelectedWebsite();
  const [items, setItems] = useState([]);
  const [report, setReport] = useState([]);
  const [form, setForm] = useState({ label: "", domain: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Per-competitor drill-down state:
  //   expandedId: which row is open
  //   mentionsCache: { [competitorId]: rows }  — avoids refetch on toggle
  const [expandedId, setExpandedId] = useState(null);
  const [mentionsCache, setMentionsCache] = useState({});
  const [mentionsLoading, setMentionsLoading] = useState(null);
  // DataForSEO's own external mention index (distinct from the scan-
  // derived data above) -- null while loading, {} on failure/not-configured.
  const [llmMentions, setLlmMentions] = useState(null);

  async function reload() {
    if (!websiteId) return;
    const [list, rep] = await Promise.all([
      api.list(websiteId),
      api.report(websiteId),
    ]);
    setItems(list.data);
    setReport(rep.data);
  }
  useEffect(() => {
    reload();
    setExpandedId(null);
    setMentionsCache({});
    setLlmMentions(null);
    if (!websiteId) return;
    llmMentionsApi
      .get(websiteId)
      .then((r) => setLlmMentions(r.data))
      .catch(() => setLlmMentions({ configured: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  if (!websiteId)
    return <div className="text-slate-500">{t("prompts.pickWebsite")}</div>;

  async function onCreate(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.create(websiteId, form);
      setForm({ label: "", domain: "" });
      await reload();
    } catch (err) {
      setError(
        err?.response?.data?.domain?.[0] ||
          err?.response?.data?.domain ||
          t("competitors.couldNotSave"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(c) {
    if (!confirm(t("competitors.removeConfirm", { label: c.label }))) return;
    await api.remove(websiteId, c.id);
    if (expandedId === c.id) setExpandedId(null);
    await reload();
  }

  async function toggle(c) {
    if (expandedId === c.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(c.id);
    if (!mentionsCache[c.id]) {
      setMentionsLoading(c.id);
      try {
        const r = await api.mentions(websiteId, c.id);
        setMentionsCache((s) => ({ ...s, [c.id]: r.data }));
      } finally {
        setMentionsLoading(null);
      }
    }
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("competitors.title")}
        </h1>
        <p className="text-sm text-slate-600">{t("competitors.subtitle")}</p>
      </div>

      <form onSubmit={onCreate} className="card grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("competitors.label")}
          </label>
          <input
            className="input mt-1"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("competitors.domain")}
          </label>
          <input
            type="text"
            className="input mt-1"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            required
            placeholder={t("competitors.domainPlaceholder")}
            autoComplete="off"
          />
        </div>
        <button className="btn-primary" disabled={busy}>
          {busy ? t("competitors.adding") : t("competitors.add")}
        </button>
        {error && (
          <div className="sm:col-span-3 text-sm text-red-600">{error}</div>
        )}
      </form>

      {items.length > 0 && (
        <div className="card">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">
            {t("competitors.chartTitle")}
          </h2>
          <CompetitorMentionsChart items={items} report={report} />
        </div>
      )}

      <LlmMentionsPanel data={llmMentions} t={t} />

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium w-px"></th>
              <th className="px-4 py-3 font-medium">{t("competitors.label")}</th>
              <th className="px-4 py-3 font-medium">{t("competitors.domain")}</th>
              <th className="px-4 py-3 font-medium">{t("competitors.colMentions")}</th>
              <th className="px-4 py-3 font-medium">
                {t("competitors.colMentionRate")}
              </th>
              <th className="px-4 py-3 font-medium">{t("competitors.colSov")}</th>
              <th className="px-4 py-3 font-medium">{t("competitors.colDomainRating")}</th>
              <th className="px-4 py-3 font-medium">{t("competitors.colOrganicTraffic")}</th>
              <th className="px-4 py-3 font-medium">{t("competitors.colReferringDomains")}</th>
              <th className="px-4 py-3 font-medium w-px"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {items.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-4 py-6 text-slate-500">
                  {t("competitors.noneYet")}
                </td>
              </tr>
            ) : (
              items.flatMap((c) => {
                const r = report.find((x) => x.competitor_id === c.id);
                const isOpen = expandedId === c.id;
                const rows = mentionsCache[c.id] || [];
                return [
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 align-top">
                      <button
                        onClick={() => toggle(c)}
                        className="text-slate-500 hover:text-slate-900"
                        aria-label="toggle"
                      >
                        {isOpen ? "▾" : "▸"}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <span className="inline-flex items-center gap-2">
                        <SiteFavicon
                          url={c.favicon_url}
                          domain={c.domain}
                          label={c.label}
                          size={18}
                        />
                        {c.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.domain}</td>
                    <td className="px-4 py-3 mono">{r?.mention_count ?? 0}</td>
                    <td className="px-4 py-3 mono">
                      {r ? `${r.mention_rate}%` : "—"}
                    </td>
                    <td className="px-4 py-3 mono">
                      {r ? `${r.share_of_voice}%` : "—"}
                    </td>
                    <td className="px-4 py-3 mono">
                      {r?.ahrefs_domain_rating ?? "—"}
                    </td>
                    <td className="px-4 py-3 mono">
                      {r?.ahrefs_organic_traffic?.toLocaleString() ?? "—"}
                    </td>
                    <td className="px-4 py-3 mono">
                      {r?.ahrefs_referring_domains?.toLocaleString() ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(c)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        {t("common.remove")}
                      </button>
                    </td>
                  </tr>,
                  isOpen && (
                    <tr key={`${c.id}-x`} className="bg-slate-50">
                      <td colSpan="10" className="px-4 py-4 text-sm">
                        <div className="text-xs uppercase text-slate-500 mb-2">
                          {t("competitors.mentionsTitle", { label: c.label })}
                        </div>
                        {mentionsLoading === c.id ? (
                          <div className="text-slate-500">{t("common.loading")}</div>
                        ) : rows.length === 0 ? (
                          <div className="text-slate-500">
                            {t("competitors.noMentionsYet")}
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="text-left text-slate-500">
                                <tr>
                                  <th className="px-3 py-2 font-medium">
                                    {t("answers.colPrompt")}
                                  </th>
                                  <th className="px-3 py-2 font-medium">
                                    {t("answers.colModel")}
                                  </th>
                                  <th className="px-3 py-2 font-medium">
                                    {t("answers.colSentiment")}
                                  </th>
                                  <th className="px-3 py-2 font-medium">
                                    {t("answers.colWhen")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((m, i) => (
                                  <tr
                                    key={i}
                                    className="border-t border-slate-200"
                                  >
                                    <td className="px-3 py-2 max-w-md">
                                      {m.prompt_text || "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                      <ModelBadge modelKey={m.ai_model} size="sm" />
                                    </td>
                                    <td className="px-3 py-2">
                                      <span
                                        className={`text-xs px-2 py-0.5 rounded-full ring-1 sentiment-${m.sentiment || "unknown"}`}
                                      >
                                        {m.sentiment || "—"}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-slate-500">
                                      {m.completed_at
                                        ? new Date(m.completed_at).toLocaleString()
                                        : "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  ),
                ].filter(Boolean);
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── LLM Mentions (DataForSEO's external index) ───────────────── */
/* Distinct from CompetitorMentionsChart above, which counts mentions
   inside THIS client's own completed scans. This panel shows
   DataForSEO's own, broader mention index for the domain -- doesn't
   cost tokens, isn't derived from ScanResult at all. */

function LlmMentionsPanel({ data, t }) {
  if (!data) {
    return (
      <div className="card text-sm text-slate-500">{t("common.loading")}</div>
    );
  }
  if (!data.configured) return null;

  const total = data.aggregated_metrics?.total?.mentions || 0;
  const volume = data.aggregated_metrics?.total?.ai_search_volume || 0;
  const sources = (data.aggregated_metrics?.sources_domain || []).slice(0, 6);

  return (
    <div className="card space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">
          {t("competitors.llmMentionsTitle")}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {t("competitors.llmMentionsSubtitle")}
        </p>
      </div>

      {data.limited_coverage && (
        // A caveat, not a hard stop -- the US/English data below might
        // still contain something real for a non-English-market brand,
        // so it stays visible; this just sets accurate expectations.
        <div className="text-sm text-slate-500 bg-slate-50 rounded-md px-3 py-2">
          {t("competitors.llmMentionsNoCoverage")}
        </div>
      )}
      {total === 0 ? (
        <div className="text-sm text-slate-500">{t("competitors.llmMentionsNone")}</div>
      ) : (
        <>
          <div className="flex gap-8">
            <div>
              <div className="text-2xl font-semibold text-slate-900 mono">
                {total.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500">{t("competitors.llmMentionsCount")}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-slate-900 mono">
                {volume.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500">{t("competitors.llmMentionsVolume")}</div>
            </div>
          </div>
          {sources.length > 0 && (
            <div>
              <div className="text-xs uppercase text-slate-500 mb-2">
                {t("competitors.llmMentionsTopSources")}
              </div>
              <ul className="space-y-1.5">
                {sources.map((s) => (
                  <li
                    key={s.key}
                    className="flex items-center justify-between text-sm rounded-md bg-slate-50 px-3 py-1.5"
                  >
                    <span className="text-slate-700 truncate">{s.key}</span>
                    <span className="mono text-slate-500 shrink-0 ml-3">
                      ×{s.mentions}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CompetitorMentionsChart({ items, report }) {
  const rows = items
    .map((c) => {
      const r = report.find((x) => x.competitor_id === c.id);
      return { label: c.label, mentions: r?.mention_count ?? 0 };
    })
    .sort((a, b) => b.mentions - a.mentions);

  return (
    <ResponsiveContainer width="100%" height={Math.max(120, rows.length * 40)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 5, right: 20, bottom: 0, left: 10 }}>
        <CartesianGrid stroke="#eef2f7" horizontal={false} />
        <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} allowDecimals={false} />
        <YAxis type="category" dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} width={120} />
        <Tooltip
          contentStyle={{
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        />
        <Bar dataKey="mentions" fill="#2ea7af" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
